import { memo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../../../locales/messages";
import { reviewTokens } from "../../../theme";
import { isImageUrlValue } from "../manualSignalCr";
import { LazyThumb } from "../../LazyThumb";
import { isPromotionTagField } from "./promotionTagDisplay";
import { PromotionTagValue } from "./PromotionTagValue";

export const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export const isImageFieldValue = (
  fieldKey: string | undefined,
  v: unknown,
): boolean => fieldKey === "image" && isImageUrlValue(v);

export interface ValuePaneProps {
  fieldKey: string | undefined;
  value: unknown;
  variant: "before" | "after";
  imageSize?: number;
}

export const ValuePane = memo(function ValuePane({
  fieldKey,
  value,
  variant,
  imageSize = 56,
}: ValuePaneProps) {
  const { t } = useTranslation();
  const isAfter = variant === "after";

  const renderValue = () => {
    if (isImageFieldValue(fieldKey, value)) {
      return (
        <Box
          sx={{
            width: imageSize,
            height: imageSize,
            borderRadius: `${reviewTokens.radiusSm}px`,
            border: `1px solid ${reviewTokens.border}`,
            overflow: "hidden",
          }}
        >
          <LazyThumb src={value as string} size={imageSize} fit="cover" />
        </Box>
      );
    }
    if (isPromotionTagField(fieldKey)) {
      return <PromotionTagValue value={value} />;
    }
    const text = formatValue(value);
    const isJson = typeof value === "object" && value !== null;
    return (
      <Tooltip title={text.length > 80 ? text : ""} placement="top">
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-word",
            fontFamily: isJson ? reviewTokens.fontMono : undefined,
            fontSize: isJson ? 12 : 13,
            color:
              value === null || value === undefined
                ? "text.disabled"
                : isAfter
                  ? "text.primary"
                  : "text.secondary",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.25,
        borderRadius: `${reviewTokens.radiusMd}px`,
        bgcolor: isAfter ? "background.paper" : reviewTokens.slate50,
        border: `1px solid ${reviewTokens.border}`,
        borderLeft: isAfter ? `3px solid ${reviewTokens.brand}` : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
          display: "block",
          mb: 0.5,
          lineHeight: 1,
        }}
      >
        {isAfter ? t(messages.After()) : t(messages.Before())}
      </Typography>
      {renderValue()}
    </Box>
  );
});
